import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import {
  usePublicSubprocessors,
  type PublicSubprocessor,
} from "@/api/queries/subprocessors";
import { DEFAULT_LANG, isSupportedLang } from "@/i18n";
import { formatCalendarDate } from "@/lib/labels";
import { useSeo } from "@/lib/seo";

/**
 * Veřejný seznam zpracovatelů — čl. 28 odst. 2 a 4 GDPR.
 *
 * Vypisuje každou třetí stranu, která pro platformu zpracovává osobní
 * údaje, plus právní základ případného předání mimo EHP (SCC =
 * standardní smluvní doložky, DPF = rámec EU-US Data Privacy Framework).
 *
 * **Data přicházejí z API, ne z tohoto souboru.** Do 2026-08-19 tu
 * stálo pole sedmi konstant a natvrdo psané datum „poslední
 * aktualizace" (KAN-125). Mělo to dvě vady, obě podstatné u dokumentu,
 * který musí být aktuální ze zákona:
 *
 * * výměna dodavatele znamenala **deploy**, a
 * * to datum nemělo s obsahem žádnou vazbu, takže mohlo tvrdit cokoli.
 *
 * Seznam teď udržuje platform admin (``/admin/platform/subprocessors``)
 * a „poslední aktualizace" je ``max(updated_at)`` přes zobrazené řádky.
 * Vyřazený dodavatel se **nemaže**, jen deaktivuje: seznam je doklad
 * o tom, kdo měl kdy přístup k údajům. Od KAN-1937 ho stránka ukazuje
 * se stavem „Vyřazen" — slibuje to zpracovatelská smlouva (čl. 7.2).
 *
 * Služby, které zpracovateli nejsou (``is_processor = false``: veřejný
 * rejstřík ARES, přihlášení přes Google), mají od KAN-1938 vlastní
 * sekci „Další služby" a nepočítají se mezi subzpracovatele.
 */
export default function Subprocessors(): JSX.Element {
  const { t, i18n } = useTranslation("legal");
  const { lang: langParam } = useParams<{ lang: string }>();
  const lang = isSupportedLang(langParam) ? langParam : DEFAULT_LANG;
  // KAN-1955 — stránka neměla titulek ani popisek pro vyhledávače.
  useSeo({
    title: t("subprocessors.title"),
    description: t("subprocessors.lead"),
    path: "/legal/subprocessors",
    lang,
  });
  const query = usePublicSubprocessors();
  const items = query.data?.items ?? [];
  const lastUpdated = query.data?.last_updated ?? null;
  // KAN-1938 — ARES nebo přihlášení přes Google zpracovateli nejsou.
  // V jedné tabulce pod nadpisem „zpracovávají osobní údaje" by to
  // tvrdily, proto mají vlastní sekci.
  const processors = items.filter((p) => p.is_processor);
  const otherServices = items.filter((p) => !p.is_processor);

  return (
    <main className="mx-auto max-w-4xl px-4 py-12">
      <h1 className="mb-2 text-3xl font-bold text-ink">
        {t("subprocessors.title")}
      </h1>
      <p className="mb-8 text-sm text-ink-2">
        {t("subprocessors.lead")}
      </p>

      {processors.length > 0 && (
        <SubprocessorTable
          items={processors}
          testId="subprocessor-list-table"
          linkHeader={t("subprocessors.col.dpa")}
          linkLabel={t("subprocessors.dpaLink")}
        />
      )}

      {otherServices.length > 0 && (
        <section className="mt-10" data-testid="subprocessor-other-services">
          <h2 className="mb-2 text-xl font-semibold text-ink">
            {t("subprocessors.other.title")}
          </h2>
          <p className="mb-4 text-sm text-ink-2">{t("subprocessors.other.lead")}</p>
          <SubprocessorTable
            items={otherServices}
            testId="subprocessor-other-table"
            linkHeader={t("subprocessors.other.colLink")}
            linkLabel={t("subprocessors.other.link")}
          />
        </section>
      )}

      {/* Prázdný seznam je tvrzení „nepoužíváme žádného zpracovatele",
          což u běžící platformy není pravda — proto se rozliší od
          „ještě se to načítá". */}
      {!query.isPending && items.length === 0 && (
        <p className="mt-6 text-sm text-ink-3" data-testid="subprocessor-empty">
          {t("subprocessors.empty")}
        </p>
      )}

      {/* Bez data se raději nevypíše nic. Věta „Poslední aktualizace: "
          bez data vypadá jako rozbitá stránka; ručně dosazené „dnes" by
          bylo horší — lhalo by. */}
      {lastUpdated && (
        <p className="mt-8 text-xs text-ink-3" data-testid="subprocessor-updated">
          {t("subprocessors.lastUpdated", {
            // KAN-1937 — datum ve formátu jazyka stránky („6. 9. 2026"),
            // ne strojové „2026-09-06".
            date: formatCalendarDate(lastUpdated, i18n.language),
          })}
        </p>
      )}
    </main>
  );
}

function SubprocessorTable({
  items,
  testId,
  linkHeader,
  linkLabel,
}: {
  items: PublicSubprocessor[];
  testId: string;
  linkHeader: string;
  linkLabel: string;
}): JSX.Element {
  const { t } = useTranslation("legal");
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testId}>
        <thead>
          <tr className="border-b border-border-strong text-left text-xs font-semibold uppercase tracking-wide text-ink-2">
            <th className="px-3 py-2">{t("subprocessors.col.provider")}</th>
            <th className="px-3 py-2">{t("subprocessors.col.country")}</th>
            <th className="px-3 py-2">{t("subprocessors.col.purpose")}</th>
            <th className="px-3 py-2">{t("subprocessors.col.transfer")}</th>
            <th className="px-3 py-2">{t("subprocessors.col.status")}</th>
            <th className="px-3 py-2">{linkHeader}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr
              key={p.key}
              className="border-b border-border align-top"
              data-testid={`subprocessor-row-${p.key}`}
            >
              <td className="px-3 py-2 font-medium">{p.name}</td>
              <td className="px-3 py-2 text-ink-2">{p.country}</td>
              <td className="px-3 py-2 text-ink-2">{p.purpose}</td>
              <td className="px-3 py-2 text-ink-2">{p.transfer_mechanism}</td>
              <td
                className={
                  p.is_active ? "px-3 py-2 text-ink-2" : "px-3 py-2 text-ink-3"
                }
                data-testid={`subprocessor-status-${p.key}`}
              >
                {p.is_active
                  ? t("subprocessors.status.active")
                  : t("subprocessors.status.inactive")}
              </td>
              <td className="px-3 py-2">
                {/* Služba bez smlouvy (ARES) nemá co odkázat — pomlčka,
                    ne mrtvý odkaz (KAN-1938). */}
                {p.dpa_url ? (
                  <a
                    href={p.dpa_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-ink underline hover:no-underline"
                    aria-label={`${linkLabel}: ${p.name}`}
                  >
                    {linkLabel}
                  </a>
                ) : (
                  <span className="text-ink-3">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
